/*
 * Copyright (c) 2024 Paul Ostrowski. All rights reserved.
 *
 * This software is the proprietary information of Paul Ostrowski.
 * Unauthorized copying, modification, or distribution of this code, via any medium, is strictly prohibited.
 * This code is proprietary and confidential.
 *
 * For licensing inquiries, contact Paul Ostrowski at postrowski@gmail.com.
*/

import React, { useState, useEffect, useRef } from 'react';
import Speed from "model/stats/Speed";

interface SpeedSelectorProps {
  speed: Speed;
  onSave: (newSpeed: Speed) => void;
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
}

const SpeedSelector: React.FC<SpeedSelectorProps> = ({
  speed,
  onSave,
  isOpen,
  setIsOpen
}) => {
  const [speedAttributes, setSpeedAttributes] = useState<Speed>(speed);

  const panelRef = useRef<HTMLSpanElement>(null);
  const textRef = useRef<HTMLSpanElement>(null);
  const titleRef = useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    // Merge with provided speed attributes whenever speed prop changes
    setSpeedAttributes({
      walk: speed?.walk || 0,
      burrow: speed?.burrow || 0,
      climb: speed?.climb || 0,
      swim: speed?.swim || 0,
      fly: speed?.fly || 0,
      hover: speed?.hover || false,
      notes: speed?.notes || ''
    });
  }, [speed]);

  const handleChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    attribute: keyof Speed
  ) => {
    const value =
      event.target.type === 'checkbox'
        ? event.target.checked
        : event.target.type === 'number'
        ? Math.max(0, parseInt(event.target.value, 10))
        : event.target.value;

    setSpeedAttributes((prevAttributes) => ({
      ...prevAttributes,
      [attribute]: value,
    }));
    onSave({ ...speedAttributes, [attribute]: value });
  };

  const handleClickOutside = (event: MouseEvent) => {
    if (
      panelRef.current && !panelRef.current.contains(event.target as Node) &&
      textRef.current  && !textRef.current.contains(event.target as Node) &&
      titleRef.current && !titleRef.current.contains(event.target as Node)
    ) {
      setIsOpen(false);
    }
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      // Cleanup code when the component is unmounted
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <div className="property-line">
      <h4 onMouseDown={() => setIsOpen(true)} ref={titleRef}>
        Speed
      </h4>
      &nbsp;
      <span className="multi-select-container edit-field">
        <span
          className="property-line"
          onMouseDown={() => setIsOpen(true)}
          ref={textRef}
        >
          <span className="property-line">
            {Object.entries(speedAttributes)
              .map(([key, value]) => {
                if (value && key !== 'hover' && key !== 'notes') {
                  const displayKey = key === 'walk' ? '' : `${key}:\u00A0`;
                  return displayKey + value + '\u00A0ft.';
                }
                return null;
              })
              .filter(Boolean)
              .join(', ')}
            {speedAttributes.hover ? '\u00A0(hover)' : ''}
            {speedAttributes.notes ? ', ' + speedAttributes.notes : ''}
          </span>
        </span>
        {isOpen && (
          <span className="multi-select-panel speed-select-panel" ref={panelRef}>
            {Object.entries(speedAttributes).map(([key, value]) => (
              <div key={key}>
                {key === 'hover' ? (
                  <>
                    <span className="speed-input-key">
                      {key.substring(0, 1).toUpperCase() + key.substring(1)}
                    </span>
                    <input
                      type="checkbox"
                      checked={value as boolean}
                      onChange={(event) => handleChange(event, key as keyof Speed)}
                    />
                  </>
                ) : (
                  <>
                    <span className="speed-input-key">
                      {key.substring(0, 1).toUpperCase() + key.substring(1)}
                    </span>
                    <input
                      className={'speed-input' + (key === 'notes' ? ' speed-input-text' : '')}
                      type={key === 'notes' ? 'text' : 'number'}
                      step="5"
                      value={value as string | number}
                      onChange={(event) => handleChange(event, key as keyof Speed)}
                    />
                    {key === 'notes' ? '' : 'ft.'}
                  </>
                )}
              </div>
            ))}
          </span>
        )}
      </span>
    </div>
  );
};

export default SpeedSelector;
